import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/VentasList.css';
import VentasChart from '../components/VentasChart';

export default function VentasList() {
  const { userProfile, isAdminMode } = useAuth();

  // ======================
  // ESTADOS
  // ======================
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [searchPedido, setSearchPedido] = useState('');
  const [searchSimo, setSearchSimo] = useState('');
  const [searchCedula, setSearchCedula] = useState('');

  // ======================
  // FETCH DE VENTAS
  // ======================
  const fetchVentas = () => {
    setLoading(true);

    const shouldFilterByAgent = !isAdminMode();
    const agentParam =
      shouldFilterByAgent && userProfile?.agenteId
        ? `&agenteId=${userProfile.agenteId}`
        : '';

    const url =
      filter === 'all'
        ? `/api/ventas?limit=500${agentParam}`
        : `/api/ventas?limit=500&categoria=${filter}${agentParam}`;

    axios
      .get(url)
      .then(res => {
        setItems(res.data.items || []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchVentas();
  }, [filter, userProfile]);

  // ======================
  // FILTRADO (PRIMERO)
  // ======================
    const filteredItems = useMemo(() => {
      return items.filter(it => {
        const matchPedido = searchPedido === '' || (it.pedido && it.pedido.includes(searchPedido));
        const matchSimo = searchSimo === '' || (it.simo && it.simo.includes(searchSimo));
        const matchCedula = searchCedula === '' || (it.cedulaCliente && it.cedulaCliente.includes(searchCedula));
        return matchPedido && matchSimo && matchCedula;
      });
    }, [items, searchPedido, searchSimo, searchCedula]);

  // ======================
  // AGRUPACIONES (DESPUÉS)
  // ======================
  const renovaciones = filteredItems.filter(it => it.esRenovacion);

  const prepago = filteredItems.filter(it => {
    return (
      !it.esRenovacion &&
      it.plan &&
      (it.plan.toLowerCase().includes('prepago') ||
        it.plan.toLowerCase().includes('kit prepago'))
    );
  });

  // Hogar: ventas con categoria o tipoPedido 'hogar'
  const hogar = filteredItems.filter(
    it => (it.categoria === 'hogar' || it.tipoPedido === 'hogar') && !it.esRenovacion
  );

  // Ventas nuevas: las que no son renovaciones, ni prepago, ni hogar
  const nuevas = filteredItems.filter(
    it => !it.esRenovacion && !prepago.includes(it) && !hogar.includes(it)
  );

  // ======================
  // PAGINACIÓN (5 por página)
  // ======================
  const PAGE_SIZE = 5;
  const [pageRenov, setPageRenov] = useState(1);
  const [pagePrep, setPagePrep] = useState(1);
  const [pageNew, setPageNew] = useState(1);

  const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / PAGE_SIZE));
  const slicePage = (arr, page) => arr.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function renderPager(page, setPage, arr) {
    const total = totalPages(arr);
    return (
      <div className="pagination">
        <button onClick={() => setPage(1)} disabled={page === 1}>{'«'}</button>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>{'‹'}</button>
        <span className="page-numbers">Página {page} de {total}</span>
        <button onClick={() => setPage(Math.min(total, page + 1))} disabled={page === total}>{'›'}</button>
        <button onClick={() => setPage(total)} disabled={page === total}>{'»'}</button>
      </div>
    );
  }

  // ======================
  // ACCIONES
  // ======================
  const startEdit = item => {
    setEditingItem(item);
    setEditForm({
      plan: item.plan || '',
      planPrice: item.planPrice || '',
      cedulaCliente: item.cedulaCliente || '',
      customerName: item.customerName || '',
      imei: item.imei || '',
      accesorioSerie: item.accesorioSerie || '',
      homeNumber: item.homeNumber || ''
    });
  };

  const handleEdit = async () => {
    try {
      const cat =
        editingItem.categoria ||
        (editingItem.tipoPedido === 'hogar' ? 'hogar' : 'movil');

      await axios.put(
        `/api/ventas/${editingItem.id}?categoria=${cat}`,
        editForm
      );

      setItems(items =>
        items.map(it =>
          it.id === editingItem.id ? { ...it, ...editForm } : it
        )
      );

      setEditingItem(null);
      setEditForm({});
    } catch (err) {
      alert(
        'Error al editar: ' +
          (err.response?.data?.error || err.message)
      );
    }
  };

  const handleDelete = async item => {
    try {
      const cat =
        item.categoria || (item.tipoPedido === 'hogar' ? 'hogar' : 'movil');

      await axios.delete(`/api/ventas/${item.id}?categoria=${cat}`);

      setItems(items => items.filter(it => it.id !== item.id));
      setDeleteConfirm(null);
    } catch (err) {
      alert(
        'Error al eliminar: ' +
          (err.response?.data?.error || err.message)
      );
    }
  };

  const formatDate = createdAt => {
    if (!createdAt) return '-';
    const seconds = createdAt._seconds || createdAt.seconds;
    if (seconds) {
      return new Date(seconds * 1000).toLocaleDateString('es-CR');
    }
    return new Date(createdAt).toLocaleDateString('es-CR');
  };

  // ======================
  // RENDER CARD
  // ======================
  const renderVentaCard = it => {
    const isHogar = it.categoria === 'hogar' || it.tipoPedido === 'hogar';

    return (
      <div key={it.id} className={`venta-card ${isHogar ? 'hogar' : 'movil'}`}>
        <div className="venta-card-header">
          <div className={`venta-type-badge ${isHogar ? 'hogar' : 'movil'}`}>
            {isHogar ? '🏠 Hogar' : '📱 Móvil'}
          </div>
          <div className="venta-actions">
            <button className="action-btn edit" onClick={() => startEdit(it)}>
              ✏️
            </button>
            <button
              className="action-btn delete"
              onClick={() => setDeleteConfirm(it)}
            >
              🗑️
            </button>
          </div>
        </div>

        <div className="venta-plan">{it.plan}</div>

        <div className="venta-detail">
          <span className="venta-detail-label">Agente:</span>
          <span>{it.agenteId}</span>
        </div>

        {it.customerName && (
          <div className="venta-detail">
            <span className="venta-detail-label">Cliente:</span>
            <span>{it.customerName}</span>
          </div>
        )}

        {it.cedulaCliente && (
          <div className="venta-detail">
            <span className="venta-detail-label">Cédula:</span>
            <span>{it.cedulaCliente}</span>
          </div>
        )}

        {!isHogar && it.imei && (
          <div className="venta-detail">
            <span className="venta-detail-label">IMEI:</span>
            <span>{it.imei}</span>
          </div>
        )}

        {it.homeNumber && (
          <div className="venta-detail">
            <span className="venta-detail-label">
              {isHogar ? 'Orden Simo:' : 'Teléfono:'}
            </span>
            <span>{it.homeNumber}</span>
          </div>
        )}

        <div className="venta-detail">
          <span className="venta-detail-label">Fecha:</span>
          <span>{formatDate(it.createdAt)}</span>
        </div>

        {it.planPrice && (
          <div className="venta-price">
            ₡{Number(it.planPrice).toLocaleString()}
            <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>
              (sin IVA)
            </span>
          </div>
        )}
      </div>
    );
  };

  // ======================
  // RENDER
  // ======================
  return (
    <div className="ventas-list-container">
      <VentasChart renovaciones={renovaciones} prepago={prepago} hogar={hogar} nuevas={nuevas} />
      <div className="ventas-header">
        <h2>Listado de Ventas</h2>
        <div className="ventas-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            📊 Todas
          </button>
          <button
            className={`filter-btn ${filter === 'movil' ? 'active' : ''}`}
            onClick={() => setFilter('movil')}
          >
            📱 Móvil
          </button>
          <button
            className={`filter-btn ${filter === 'hogar' ? 'active' : ''}`}
            onClick={() => setFilter('hogar')}
          >
            🏠 Hogar
          </button>
        </div>
      </div>

      <div className="search-filters">
        <input
          placeholder="Nº Pedido"
          value={searchPedido}
          onChange={e => setSearchPedido(e.target.value)}
        />
        <input
          placeholder="Orden Simo"
          value={searchSimo}
          onChange={e => setSearchSimo(e.target.value)}
        />
        <input
          placeholder="Cédula"
          value={searchCedula}
          onChange={e => setSearchCedula(e.target.value)}
        />
      </div>

      <h3>🔄 Renovaciones</h3>
      {loading ? (
        <div className="empty-ventas">Cargando...</div>
      ) : renovaciones.length === 0 ? (
        <div className="empty-ventas">No hay renovaciones</div>
      ) : (
        <>
          <div className="ventas-grid">
            {slicePage(renovaciones, pageRenov).map(renderVentaCard)}
          </div>
          {renderPager(pageRenov, setPageRenov, renovaciones)}
        </>
      )}

      <h3>💳 Prepago</h3>
      {prepago.length === 0 ? (
        <div className="empty-ventas">No hay ventas prepago</div>
      ) : (
        <>
          <div className="ventas-grid">{slicePage(prepago, pagePrep).map(renderVentaCard)}</div>
          {renderPager(pagePrep, setPagePrep, prepago)}
        </>
      )}

      <h3>🆕 Ventas Nuevas</h3>
      {nuevas.length === 0 ? (
        <div className="empty-ventas">No hay ventas nuevas</div>
      ) : (
        <>
          <div className="ventas-grid">{slicePage(nuevas, pageNew).map(renderVentaCard)}</div>
          {renderPager(pageNew, setPageNew, nuevas)}
        </>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Confirmar eliminación</h3>
            <p>{deleteConfirm.plan}</p>
            <button onClick={() => handleDelete(deleteConfirm)}>
              Eliminar
            </button>
            <button onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {editingItem && (
        <div className="modal-overlay" onClick={() => setEditingItem(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Editar venta</h3>
            <input
              value={editForm.plan}
              onChange={e =>
                setEditForm({ ...editForm, plan: e.target.value })
              }
            />
            <button onClick={handleEdit}>Guardar</button>
          </div>
        </div>
      )}
    </div>
  );
}
